/* eslint-disable no-param-reassign */

import Backbone from 'backbone';
import $ from 'jquery';
import { pgridFactory } from '@bingads-webui-campaign/pgrid-factory';
import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { editablePlugin } from '@bingads-webui-campaign/projection-grid-editable';
import { inlineErrorPlugin } from '@bingads-webui/projection-grid-inline-error';
import { updateGridQuery } from '@bingads-webui-campaign/summary-grid-helper';
import { inlineDownloadWithoutFilterstateAndSegmentPlugin } from '@bingads-webui-campaign/projection-grid-inline-download';
import { DeterministicView } from '@bingads-webui/deterministic-view';
import * as putil from '@bingads-webui/primitive-utilities';
import { paginationPlugin } from '@bingads-webui/projection-grid-pagination';
import { placeholderPlugin } from '@bingads-webui/projection-grid-placeholder';
import { toolbarPlugin } from '@bingads-webui/projection-grid-toolbar';
import { uiBlockerPlugin } from '@bingads-webui/projection-grid-uiblocker';
import { perfMarkerPlugin } from '@bingads-webui-campaign/pgrid-performance-log';
import { getHotelCenterService } from '@bingads-webui-campaign/hotel-center-service';
import { BulkEditView } from './bulk-edit/bulk-edit-view';
import * as bulkEditToast from './bulk-edit/bulk-edit-toast';
import { BulkEditPanelBase } from './bulk-edit/bulk-edit-panel-base';
import BulkEditBidMultiPanel from './bulk-edit/bulk-edit-bid-multi-panel';
import BulkEditBidPanel from './bulk-edit/bulk-edit-bid-panel';
import BulkIncreaseDecreaseBidPanel from './bulk-edit/increase-decrease-bid-panel';
import CountryCodeGridOptions from './grid-options/country-code-grid-options';
import HotelDateGridOptions from './grid-options/hotel-date-grid-options';
import HotelDeviceTypeGridOptions from './grid-options/hotel-devicetype-grid-options';
import HotelGroupGridOptions from './grid-options/hotel-group-grid-options';
import HotelIdGridOptions from './grid-options/hotel-id-grid-options';
import HotelSubAccountGridOptions from './grid-options/hotel-subaccount-grid-options';
import LengthOfStayGridOptions from './grid-options/length-stay-grid-options';
import PublisherCountryIdGridOptions from './grid-options/publisher-country-id-grid-options';
import SlotTypeGridOptions from './grid-options/slot-type-grid-options';
import HotelAdvancedBookingGridOptions from './grid-options/advanced-booking-grid-options';
import HotelCheckinDayGridOptions from './grid-options/checkin-day-grid-options';
import DateTypeGridOptions from './grid-options/date-type-grid-options';
import SiteTypeGridOptions from './grid-options/site-type-grid-options';
import template from './hotel-center-overview-template.pug';
import {
  BidCapPanelView,
  BudgetPanelView,
  SubAccountBar,
  SubAccountBidEdit,
  SubAccountBidMultipliersEdit,
} from './subaccount-bar/subaccount-bar';
import {
  HotelBidInlineEditView,
  HotelGroupBidInlineEditView,
  SubAccountBidInlineEditView,
} from './inline-edit/bid-inline-edit-view';
import {
  HotelBidMultipliersInlineEditView,
  HotelGroupBidMultipliersInlineEditView,
  SubAccountBidMultipliersInlineEditView,
} from './inline-edit/multiplier-inline-edit-view';
import { conversionColumnList } from './grid-options/hotel-center-conversion-columns';
import {
  HotelGroupInformationView,
  dateFilterPlugin,
  fetchPreferenceConfig as fetchPreference,
  instrumentation,
  toolbarTemplate,
} from './hotel-center-shared';
import { gridOptionUtils } from './hotel-center-service';
import { getDefaultPlugins } from './plugin-config';

export {
  BidCapPanelView,
  BudgetPanelView,
  BulkEditView,
  bulkEditToast,
  BulkEditPanelBase,
  BulkEditBidPanel,
  BulkEditBidMultiPanel,
  BulkIncreaseDecreaseBidPanel,
  SubAccountBar,
  SubAccountBidEdit,
  SubAccountBidMultipliersEdit,
  HotelBidInlineEditView,
  HotelGroupBidInlineEditView,
  SubAccountBidInlineEditView,
  HotelBidMultipliersInlineEditView,
  HotelGroupBidMultipliersInlineEditView,
  SubAccountBidMultipliersInlineEditView,
};

const legacyI18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/hotel-center');
const columnChooserPlugin = globalContext.requireDefault('component/bingads-grid/plugin/column-chooser');
const { crossPageSelectAllPlugin } = globalContext.require('component/bingads-grid/plugin/cross-page-select-all/index');
const { newPerformanceLogPlugin } = globalContext.require('component/bingads-grid/plugin/new-performance-log');
const state = globalContext.require('component/state/index');
const appConfig = globalContext.require('component/config/index');
const jsdata = globalContext.require('campaignui/feature/hotel-center-js-data/index');
const edm = globalContext.requireDefault('campaignui/feature/hotel-center-edm/index');
const { AccountService } = getHotelCenterService({ state, edm, jsdata });

const hotelCenterOverviewGridMap = {
  'grid-chooser-item-hotel-id': {
    gridName: 'HotelIdGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_Id_Label',
  },
  'grid-chooser-item-hotel-group': {
    gridName: 'HotelGroupGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_Group_Label',
  },
  'grid-chooser-item-subaccount': {
    gridName: 'HotelSubAccountGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_SubAccount_Label',
  },
  'grid-chooser-item-devicetype': {
    gridName: 'HotelDeviceTypeGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_DeviceType_Label',
  },
  'grid-chooser-item-date': {
    gridName: 'HotelDateGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_Date_Label',
  },
  'grid-chooser-item-length-of-stay': {
    gridName: 'HotelLengthOfStayGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_Length_Of_Stay_Label',
  },
  'grid-chooser-item-user-country': {
    gridName: 'HotelUserCountryGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_User_Country_Label',
  },
  'grid-chooser-item-slot-type': {
    gridName: 'HotelSlotTypeGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_Slot_Type_Label',
  },
  'grid-chooser-item-hotel-country': {
    gridName: 'HotelCountryGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Hotel_Contry_Label',
  },
  'grid-chooser-item-advanced-booking': {
    gridName: 'AdvancedBookingGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Advanced_Booking_Label',
  },
  'grid-chooser-item-checkin-day': {
    gridName: 'CheckinDayGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_Checkin_Day_Label',
  },
  'grid-chooser-item-date-type': {
    gridName: 'DateTypeGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_DateType_Label',
  },
  'grid-chooser-item-site-type': {
    gridName: 'SiteTypeGrid',
    linkMenuLabelKey: 'HotelCenter_Overview_Grid_Switcher_SiteType_Label',
  },
};

const biGridNames = ['HotelDeviceTypeGrid', 'HotelDateGrid', 'HotelLengthOfStayGrid',
  'HotelUserCountryGrid', 'HotelSubAccountGrid', 'HotelSlotTypeGrid', 'HotelCountryGrid',
  'AdvancedBookingGrid', 'CheckinDayGrid', 'DateTypeGrid', 'SiteTypeGrid'];


function initBulkEditBidPanel(view) {
  view.bulkEditBidPanel = new BulkEditBidPanel();
  view.setChild('bulk-bid-edit-panel-container', view.bulkEditBidPanel);

  view.bulkEditBidPanel.on('canceled', () => {
    view.$('.bulk-bid-edit-panel-container').addClass('hide');
    view.restoreToolBar();
  }, view);

  view.bulkEditBidPanel.on('saved', () => {
    view.$('.bulk-bid-edit-panel-container').addClass('hide');
    view.restoreToolBar();

    view.grid.gridView.deselectAll();
    view.grid.gridView.refresh(true);
  }, view);

  view.bulkEditBidPanel.on('error', () => {
    view.grid.gridView.refresh(true);
  }, view);
}


function initBulkEditBidMultiPanel(view) {
  view.bulkEditBidMultiPanel = new BulkEditBidMultiPanel();
  view.setChild('bulk-bid-multipliers-edit-panel-container', view.bulkEditBidMultiPanel);

  view.bulkEditBidMultiPanel.on('canceled', () => {
    view.$('.bulk-bid-multipliers-edit-panel-container').addClass('hide');
    view.restoreToolBar();
  }, view);

  view.bulkEditBidMultiPanel.on('saved', () => {
    view.$('.bulk-bid-multipliers-edit-panel-container').addClass('hide');
    view.restoreToolBar();

    view.grid.gridView.deselectAll();
    view.grid.gridView.refresh(true);
  }, view);

  view.bulkEditBidMultiPanel.on('error', () => {
    view.grid.gridView.refresh(true);
  }, view);
}


function initBulkIncreaseDecreaseBidPanel(view) {
  view.bulkIncreaseDecreaseBidPanel = new BulkIncreaseDecreaseBidPanel();
  view.setChild('bulk-increase-decrease-bid-panel-container', view.bulkIncreaseDecreaseBidPanel);

  view.bulkIncreaseDecreaseBidPanel.on('canceled', () => {
    view.$('.bulk-increase-decrease-bid-panel-container').addClass('hide');
    view.restoreToolBar();
  }, view);

  view.bulkIncreaseDecreaseBidPanel.on('saved', () => {
    view.$('.bulk-increase-decrease-bid-panel-container').addClass('hide');
    view.restoreToolBar();

    view.grid.gridView.deselectAll();
    view.grid.gridView.refresh(true);
  }, view);

  view.bulkIncreaseDecreaseBidPanel.on('error', () => {
    view.grid.gridView.refresh(true);
  }, view);
}


/**
 * Class representing a navigation tabs for HotelCenter Overview
 * @extends View
 */

export class HotelCenterOverview extends DeterministicView {
  /**
   * Initializes HotelCenterOverview
   * @returns {undefined}
   */
  initialize({
    i18n,
    fetchPreferenceConfig,
    preferenceConfig,
    preferenceService,
    serverContext,
    navigationLevels = [],
    extendPlugins = () => [],
    filterService,
    pluginFactories,
    updateSelectedSubaccount = () => {},
    gridName,
    p$daterange = null,
    refreshGridForUINext,
    jumpToReconReports,
    gridRefreshed,
    viewport,
    customHeaderOffset,
    perfMarker,
    notificationApi,
  } = {}) {
    this.i18n = i18n || legacyI18n; // use legacy i18n for integrated test
    this.gridName = gridName;
    this.p$daterange = p$daterange;
    this.serverContext = serverContext;
    this.fetchPreferenceConfig = fetchPreferenceConfig || fetchPreference;
    this.preferenceService = preferenceService;
    this.extendPlugins = extendPlugins;
    this.filterService = filterService;
    this.extendPluginFactories = pluginFactories;
    this.updateSelectedSubaccount = updateSelectedSubaccount;
    this.refreshGridForUINext = refreshGridForUINext;
    this.jumpToReconReports = jumpToReconReports;
    this.gridRefreshed = gridRefreshed;
    this.viewport = viewport;
    this.customHeaderOffset = customHeaderOffset;
    this.shouldSetUpGrid = false;
    this.isInitialGrid = true;
    this.notificationApi = notificationApi;
    this.overViewPageScenario = instrumentation.overViewPage.create();
    this.perfMarker = perfMarker;
    this.ppsPauseNotificationShowed = false;

    $('body').on('click', e => this.onBodyClicked(e));

    this.hotelGroupInformation = new HotelGroupInformationView().setState({
      hotelGroup: { Name: '' },
    });
    this.setChild('hotel-group-information-container', this.hotelGroupInformation);

    this.hotelGroupInformation.on('go-back', () => {
      state.unset('hotelGroup_drillDown');
      this.refreshGridForUINext('HotelGroupGrid');
    });

    this.hotelGroupInformation.on('remove-group-info', () => {
      state.unset('hotelGroup_drillDown');

      const entitySet = gridOptionUtils.getEntitySetByName('hotel');
      const { options } = this.grid.gridView.get('dataSource');

      options.entitySet = entitySet;
      this.showHotelGroupInformation();
      this.grid.gridView.refresh(true);
    }, this);

    if (navigationLevels.length > 0) {
      const currentLevel = navigationLevels.shift();
      const gridChooserItem = hotelCenterOverviewGridMap[currentLevel.name];
      this.model.set(gridChooserItem);
      this.setupGrid({ preferenceConfig });
      this.gridRefreshed();
    } else {
      initBulkEditBidPanel(this);
      initBulkEditBidMultiPanel(this);
      initBulkIncreaseDecreaseBidPanel(this);
    }
    $('body').on('click', '.recon-reports-link', e => this.onReconReportsLinkClicked(e));
  }

  get events() {
    return {
      'click .hotel-center-overview-toolbar-container .grid-menu-chooser-button':
        () => this.onGridMenuChooserClicked(),
      'click .grid-submenu': linkedMenu => this.onGridLinkMenuClicked(linkedMenu),
      'click .drillDownLink.HotelGroupGrid': e => this.onHotelGroupNameClicked(e, 'HotelGroupGrid'),
      'click .drillDownLink.HotelSubAccountGrid': e => this.onSubAccountNameClicked(e, 'HotelSubAccountGrid'),
      'click .drillDownLink.HotelIdGridHotelGroup': e => this.onHotelGroupNameClicked(e, 'HotelIdGrid'),
      'click .drillDownLink.HotelGroupGridSubAccount': e => this.onSubAccountNameClicked(e, 'HotelGroupGrid'),
      'click .drillDownLink.HotelIdGridSubAccount': e => this.onSubAccountNameClicked(e, 'HotelIdGrid'),
    };
  }

  get template() {
    return template;
  }

  renderTrunk() {
    this.filterService.refreshSource();
    this.filterService.initializeCombinedFilterPromise.then(() => {
      updateGridQuery(
        this.grid.gridView,
        this.filterService.getCombinedFilter()
      );
    });
    super.renderTrunk();

    if ($('.hotel-center-overview-toolbar-container .hotel-center-global-toolbar-containter').length === 0) {
      $('.hotel-center-overview-toolbar-container').append(toolbarTemplate());
    }
    if (state.get('subAccount_id')) {
      this.setUpSubAccountBar();
    } else {
      this.updateSelectedSubaccount('selectAllSubAccount');
      $('.visible-element .summary-bar-breadcrumb .breadcrumb-item').addClass('current-breadcrumb');
    }
    this.renderGrid();
  }

  onHotelGroupNameClicked(e, gridName) {
    const model = this.grid.gridView.itemOfElement(e.target);

    if (gridName === 'HotelGroupGrid') {
      state.set('hotelGroup_drillDown', {
        hotelGroupId: putil.get(model, 'Id'),
        hotelGroupName: putil.get(model, 'Name'),
        subAccountId: putil.get(model, 'SubAccountId') || state.get('subAccount_id'),
      });
      this.refreshGridForUINext('HotelIdGrid');
    } else {
      state.set('hotelGroup_drillDown', {
        hotelGroupId: putil.get(model, 'HotelGroupId'),
        hotelGroupName: putil.get(model, 'HotelGroupName'),
        subAccountId: putil.get(model, 'SubAccountId') || state.get('subAccount_id'),
      });
      this.showHotelGroupInformation();
      const entitySet = gridOptionUtils.getEntitySetByName('hotel', 'hotelGroup');
      const { options } = this.grid.gridView.get('dataSource');

      options.entitySet = entitySet;
      this.grid.gridView.refresh(true);
    }
  }

  onSubAccountNameClicked(e, gridName) {
    const model = this.grid.gridView.itemOfElement(e.target);
    const subAccountId = gridName === 'HotelSubAccountGrid' ? putil.get(model, 'Id') : putil.get(model, 'SubAccountId');

    // if subaccount changed, reset hotel group drill down
    if (subAccountId !== state.get('subAccount_id')) {
      state.unset('hotelGroup_drillDown');
    }

    state.set('subAccount_id', subAccountId);
    if (gridName === 'HotelGroupGrid') {
      this.onSubaccountChangedForUINext(subAccountId);
    } else {
      this.refreshGridForUINext('HotelGroupGrid');
      this.setUpSubAccountBar();
    }
  }

  onGridLinkMenuClicked(linkedMenu) {
    this.$('.grid-menu-chooser-button').parent().toggleClass('open');

    // Second class name of the clickable menu states which menu is clicked.
    const itemClassName = linkedMenu.currentTarget.classList[1];
    this.setState({ navigationLevels: [{ name: itemClassName }] });
  }

  onReconReportsLinkClicked() {
    this.notificationApi.dismiss(this.floatingBanner);
    this.jumpToReconReports();
  }

  setState({ navigationLevels }) {
    const defaultOverviewGrid = state.get('overviewGrid_key') || 'grid-chooser-item-subaccount';
    const defaultGridChooserItem = hotelCenterOverviewGridMap[defaultOverviewGrid];

    if (navigationLevels.length > 0) {
      const currentLevel = navigationLevels.shift();
      const gridChooserItem = hotelCenterOverviewGridMap[currentLevel.name];
      this.shouldSetUpGrid = !!state.get('overviewGrid_key') && (defaultOverviewGrid !== currentLevel.name);
      this.model.set(gridChooserItem);
      state.set('overviewGrid_key', currentLevel.name);
    } else {
      this.model.set(defaultGridChooserItem);
      state.set('overviewGrid_key', defaultOverviewGrid);
    }
    return this;
  }

  onGridMenuChooserClicked() {
    this.$('.grid-menu-chooser-button').parent().toggleClass('open');
  }

  showBulkChangeBid(toolbar, scope) {
    this.toolbar = toolbar;

    this.$('.bulk-bid-edit-panel-container').removeClass('hide');
    this.bulkEditBidPanel.setState({ scope });
    this.expandToolBar();
  }

  showBulkChangeBidMultipliers(toolbar, scope) {
    this.toolbar = toolbar;
    this.$('.bulk-bid-multipliers-edit-panel-container').removeClass('hide');
    this.bulkEditBidMultiPanel.setState({ scope });
    this.expandToolBar();
  }

  showBulkEditIncreseOrDecreseBids(toolbar, scope) {
    this.toolbar = toolbar;
    this.bulkIncreaseDecreaseBidPanel.setState({ scope });
    this.$('.bulk-increase-decrease-bid-panel-container').removeClass('hide');
    this.expandToolBar();
  }

  showHotelGroupInformation() {
    const drillDown = state.get('hotelGroup_drillDown');

    if (drillDown && drillDown.hotelGroupId && drillDown.hotelGroupName) {
      this.hotelGroupInformation.model.set('hotelGroup', { Name: drillDown.hotelGroupName });
      this.$('.hotel-group-information-container').removeClass('hide');
    } else if (!this.$('.hotel-group-information-container').hasClass('hide')) {
      this.$('.hotel-group-information-container').addClass('hide');
    }
  }

  expandToolBar() {
    const emptyBackboneView = new Backbone.View();

    this.toolbar.overrideWith(emptyBackboneView);
  }

  restoreToolBar() {
    this.toolbar.restoreToolbar();
    document.getElementById('bulk-edit-dropdown').click();
  }

  hideToolbarForNewUI() {
    $('.hotel-center-overview-toolbar-container').remove();
  }

  renderGrid() {
    const { gridName } = this.model.attributes;
    this.fetchPreferenceConfig().then((preferenceConfig) => {
      preferenceConfig.name = gridName;
      this.hideToolbarForNewUI();
      if (this.shouldSetUpGrid) {
        this.shouldSetUpGrid = false;
        this.setupGrid({ preferenceConfig });
        this.gridRefreshed();
      }
      if ($('.hotel-center-overview-grid-container')) {
        $('.hotel-center-overview-grid-container').empty().append(this.grid.gridView.render().$el);
        this.showHotelGroupInformation();
      }
    });
  }

  setupGrid({ preferenceConfig }) {
    const {
      gridName = this.gridName,
    } = this.model.attributes;

    let gridOptions = {};
    let gridActivity;
    const dateFilterContainer = this.$('.date-picker-container');

    switch (gridName) {
      case 'HotelGroupGrid':
        gridOptions = new HotelGroupGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          serverContext: this.serverContext,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
          viewport: this.viewport,
          customHeaderOffset: this.customHeaderOffset,
          onBulkEditChangeBids: toolbar => this.showBulkChangeBid(toolbar, 'hotelGroup'),
          onBulkEditChangeMultipliersBids: toolbar => this.showBulkChangeBidMultipliers(toolbar, 'hotelGroup'),
          onBulkEditIncreseOrDecreseBids: toolbar => this.showBulkEditIncreseOrDecreseBids(toolbar, 'hotelGroup'),
        });
        gridActivity = this.overViewPageScenario.loadHotelGroupGrid.create();
        break;

      case 'HotelIdGrid':
      {
        this.showHotelGroupInformation();

        gridOptions = new HotelIdGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          serverContext: this.serverContext,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
          viewport: this.viewport,
          customHeaderOffset: this.customHeaderOffset,
          onBulkEditChangeBids: toolbar => this.showBulkChangeBid(toolbar, 'hotel'),
          onBulkEditChangeMultipliersBids: toolbar => this.showBulkChangeBidMultipliers(toolbar, 'hotel'),
          onBulkEditIncreseOrDecreseBids: toolbar => this.showBulkEditIncreseOrDecreseBids(toolbar, 'hotel'),
        });
        gridActivity = this.overViewPageScenario.loadHotelIdGrid.create();
        break;
      }

      case 'HotelDeviceTypeGrid':
        gridOptions = new HotelDeviceTypeGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadDeviceTypeGrid.create();
        break;

      case 'HotelDateGrid':
        gridOptions = new HotelDateGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadDateGrid.create();
        break;

      case 'HotelLengthOfStayGrid':
        gridOptions = new LengthOfStayGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadLengthOfStayGrid.create();
        break;

      case 'HotelUserCountryGrid':
        gridOptions = new PublisherCountryIdGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadUserCountryGrid.create();
        break;

      case 'HotelSlotTypeGrid':
        gridOptions = new SlotTypeGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadSlotTypeGrid.create();
        break;

      case 'HotelCountryGrid':
        gridOptions = new CountryCodeGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadHotelCountryGrid.create();
        break;

      case 'AdvancedBookingGrid':
        gridOptions = new HotelAdvancedBookingGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadAdvancedBookingGrid.create();
        break;

      case 'CheckinDayGrid':
        gridOptions = new HotelCheckinDayGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadCheckinDayGrid.create();
        break;

      case 'DateTypeGrid':
        gridOptions = new DateTypeGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadDateTypeGrid.create();
        break;

      case 'SiteTypeGrid':
        gridOptions = new SiteTypeGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
        });
        gridActivity = this.overViewPageScenario.loadSiteTypeGrid.create();
        break;

      case 'HotelSubAccountGrid':
      default:
        gridOptions = new HotelSubAccountGridOptions({
          view: this,
          gridName,
          dateFilterContainer,
          serverContext: this.serverContext,
          preferenceService: this.preferenceService,
          p$daterange: this.p$daterange,
          filterService: this.filterService,
          viewport: this.viewport,
          customHeaderOffset: this.customHeaderOffset,
          i18n: this.i18n,
        });
        gridActivity = this.overViewPageScenario.loadSubAccountGrid.create();
        break;
    }
    const inlineDownload = inlineDownloadWithoutFilterstateAndSegmentPlugin;
    const pluginFactories = {
      perfMarkerPlugin,
      toolbarPlugin,
      paginationPlugin,
      dateFilterPlugin,
      columnChooserPlugin,
      placeholderPlugin,
      uiBlockerPlugin,
      inlineDownload,
      newPerformanceLogPlugin,
      editablePlugin,
      crossPageSelectAllPlugin,
      inlineErrorPlugin,
    };

    if (this.extendPluginFactories && this.extendPluginFactories.columnChooserPlugin) {
      pluginFactories.columnChooserPlugin = this.extendPluginFactories.columnChooserPlugin;
    }
    const useEditablePlugin = (gridName === 'HotelSubAccountGrid' || gridName === 'HotelIdGrid' || gridName === 'HotelGroupGrid');
    const useQuickFilterPlugin = (gridName === 'HotelIdGrid');
    const defaultPlugins = getDefaultPlugins({
      pluginFactories,
      gridOptions,
      useQuickFilterPlugin,
      useEditablePlugin,
      appConfig,
      perfMarker: this.perfMarker,
    });
    const extendedPlugins = this.extendPlugins();
    this.grid = pgridFactory({
      gridConfig: gridOptions,
      plugins: defaultPlugins.concat(extendedPlugins),
      preferenceConfig: preferenceConfig || {},
    });
    this.grid.gridView.on('willReload', () => {
      this.trigger('willReload');
    });
    this.grid.gridView.on('didUpdate', (changes) => {
      this.checkShowWarning(this.grid.gridView.itemArray);
      const { _isRendered } = this.grid.gridView;
      if (_isRendered && _.has(changes, 'sortableHeader')) {
        const orderby = _.result(changes.query, 'orderby') || _.result(this.grid.gridView.get('query'), 'orderby');
        const { sortableHeader } = changes;
        this.preferenceService.setAtUserLevel({
          Name: `WebApp.CampaignUI.${gridName}`,
          Data: {
            sortInfo: {
              orderby,
              sortableHeader,
            },
          },
        });
      }

      if (_.has(changes, 'columns')) {
        const selectedColumns = _.pluck(changes.columns, 'name');
        const withConversions = _.intersection(selectedColumns, conversionColumnList);

        gridActivity.trace(`Grid did redraw ${withConversions.length > 0 ? 'with' : 'without'} conversion columns`);
      }
    });

    this.listenTo(this.filterService, 'filter-state-changed', () => {
      updateGridQuery(this.grid.gridView, this.filterService.getCombinedFilter(), true);
    });

    if (!_.contains(biGridNames, gridName)) {
      this.grid.gridView.on('didReload', () => {
        this.grid.gridView.deselectAll();
      });
    }

    this.grid.gridView.on('subAccountBarChanged', () => {
      if (this.subAccountBar) {
        this.subAccountBar.onSubAccountBarChanged();
      }
    });

    this.grid.gridView.on('update:finished', () => {
      gridActivity.trace('Grid loading is completed');
    });

    gridActivity.trace('Grid setup is completed');
  }

  checkShowWarning(items) {
    if (_.some(items, item => item.PPSPauseType === 'Paused') && !this.ppsPauseNotificationShowed) {
      this.floatingBanner = this.notificationApi.showWarning(this.i18n.getString(_TL_('An account reconciliation report for your pay-per-stay bids is overdue – {{aTagBegin}}click here{{aTagEnd}} to submit your report to unpause your ads.'), {
        aTagBegin: '<a href="javascript:" class="recon-reports-link">',
        aTagEnd: '</a>',
      }));
      this.ppsPauseNotificationShowed = true;
    }
  }

  onSelected(selectedItems) {
    // The grid names in which bulk edit is enabled
    const enabledGrids = ['HotelIdGrid', 'HotelGroupGrid'];
    const { gridName } = super.getState();

    if (!_.contains(enabledGrids, gridName)) {
      return;
    }
    const selectionCount =
      this.grid.crossPageSelectAll && this.grid.crossPageSelectAll.isAllSelected ?
        this.grid.crossPageSelectAll.viewModel.totalRowsTotalPageCount() :
        selectedItems.length;
    const filter = this.grid.filterState.getAllFilters();

    this.bulkEditBidPanel.updateSelection({ selectionCount, selectedItems, filter });
    this.bulkEditBidMultiPanel.updateSelection({ selectionCount, selectedItems, filter });
    const bidTypeSet = _.chain(selectedItems)
      .map(item => item.Bid['@odata.type'])
      .uniq()
      .value();

    this.grid.config.gridToolbar.update({
      id: 'bulk-edit-increase-decrease-bids',
      disabled: _.size(bidTypeSet) > 1,
    });
    this.bulkIncreaseDecreaseBidPanel.updateSelection({
      selectionCount,
      selectedItems,
      bidTypeSet,
      filter,
    });
  }

  onBodyClicked(e) {
    const clickedEl = $(e.target);

    // light dismiss
    if (clickedEl.closest(this.$('.grid-groupmenu-container')).length === 0) {
      this.$('.grid-menu-chooser-button').parent().removeClass('open');
    }
  }

  refreshGrids(gridsList) {
    if (_.indexOf(gridsList, this.grid.config.name) !== -1) {
      this.grid.gridView.refresh(true);
    }
  }

  setUpSubAccountBar() {
    if ($('.hotel-center-overview-page').length === 0) {
      return;
    }
    this.subAccountBar = new SubAccountBar();

    this.listenTo(this.subAccountBar, 'subaccountChanged', (gridsList) => {
      this.refreshGrids(gridsList);
    });

    const summaryBarItemsEl = this.subAccountBar.render().el;
    $('.visible-element #custom-summary-bar-items').html(summaryBarItemsEl);
    this.updateSelectedSubaccount(state.get('subAccount_id'));
  }

  remove() {
    if (this.subAccountBar) {
      this.subAccountBar.remove();
    }
    super.remove();
  }

  onSubaccountChangedForUINext(selectSubAccountId) {
    if (selectSubAccountId === 'selectAllSubAccount') {
      state.set('subAccount_id', '');
      $('.visible-element #custom-summary-bar-items').html('');
    } else {
      state.set('subAccount_id', selectSubAccountId);
    }
    state.unset('hotelGroup_drillDown');
    if (this.gridName === 'HotelSubAccountGrid' && selectSubAccountId !== 'selectAllSubAccount') {
      this.refreshGridForUINext('HotelGroupGrid');
      this.setUpSubAccountBar();
      return;
    }
    if (!this.accountService) {
      this.accountService = new AccountService();
    }
    this.accountService.fetchSubAccountAsync().then(() => {
      if ($('.hotel-center-overview-page').length === 0) {
        return;
      }
      this.shouldSetUpGrid = true;
      this.renderTrunk();
    });
  }
}
